import {
  DocumentNode,
  LazyQueryHookOptions,
  OperationVariables,
  useLazyQuery as apolloUseLazyQuery,
} from '@apollo/client';
import { LazyQueryResult, QueryLazyOptions } from '@apollo/client/react/types/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import { memoryStorage } from '../storage';

/**
 * Hook to capture click events outside of an HTMLElement.
 *
 * @param callback Callback invoked when click happens outside of target ref.
 * @return Attach the returned ref to the target HTMLElement.
 */
export function useOnClickOutside<T extends HTMLElement>(
  callback: (event: MouseEvent | TouchEvent) => void,
  // From React 17 onward we need to receive these events in the capture phase.
  // For more context see: https://reactjs.org/blog/2020/08/10/react-v17-rc.html#fixing-potential-issues
  { capture = true }: AddEventListenerOptions = {},
): { ref: React.RefObject<T> } {
  const ref = useRef<T>(null);
  const listener = useCallback(
    (event: MouseEvent | TouchEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        callback(event);
      }
    },
    [ref, callback],
  );

  useEffect(() => {
    document.addEventListener('click', listener, { capture });

    return () => document.removeEventListener('click', listener, { capture });
  }, [ref, listener, capture]);

  return { ref };
}

/**
 * Function decorator for an event handler which stops propagation of the handled event.
 *
 * @param fn Event handler to decorate.
 */
export function stopPropagation(fn: () => void) {
  return (event: MouseEvent | TouchEvent) => {
    event.stopPropagation();
    fn();
  };
}

/**
 * Hook to capture escape key presses.
 *
 * @param callback Callback invoked when the escape key is pressed.
 */
export function useOnEscapeKeyDown(callback: () => void) {
  const listener = useCallback(
    (event: KeyboardEvent) => {
      if (event.code === 'Escape') {
        callback();
      }
    },
    [callback],
  );

  useEffect(() => {
    document.addEventListener('keydown', listener);

    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [listener]);
}

/** Hook that returns a method which provides whether the current component is mounted. */
export function useIsMounted() {
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return () => mounted.current;
}

/**
 * Returns a variant of setTimeout that is cleared when a component un-mounts.
 */
export function useSetTimeout() {
  const timeoutIDs = useRef<number[]>([]);

  useEffect(
    () => () => {
      for (const timeoutID of timeoutIDs.current) {
        window.clearTimeout(timeoutID);
      }
    },
    [],
  );

  return (callback: () => void, ms: number) => {
    const timeoutID = window.setTimeout(() => {
      const index = timeoutIDs.current.indexOf(timeoutID);
      if (index >= 0) {
        timeoutIDs.current.splice(index, 1);
      }

      callback();
    }, ms);

    timeoutIDs.current.push(timeoutID);
  };
}

/**
 * A hook to use a value from the local storage, it returns the value as read from the local storage (or the initial
 * value) and a function to persist a new value to the local storage.
 *
 * Note: values are serialized using JSON.stringify.
 */
export const useLocalStorage: UseSomeStorage = <TData extends unknown>(
  key: string,
  options: UseStorageOptions<TData> = {},
  fallbackStorage: Storage = memoryStorage,
) => {
  try {
    return useStorage(key, window.localStorage, options);
  } catch {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStorage(key, fallbackStorage, options);
  }
};

export type LazyQueryExecute<TData, TVariables extends OperationVariables> = (
  options?: QueryLazyOptions<TVariables>,
) => Promise<LazyQueryResult<TData, TVariables>>;
export type LazyQueryTuple<TData, TVariables extends OperationVariables> = [
  LazyQueryExecute<TData, TVariables>,
  LazyQueryResult<TData, TVariables>,
];

/**
 * This is a wrapper around useLazyQuery which returns a Promise on query execution.
 * See: https://github.com/apollographql/react-apollo/issues/3499.
 *
 * @param query The GraphQL query.
 * @param options The useLazyQuery options.
 */
export function useLazyQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode,
  options?: LazyQueryHookOptions<TData, TVariables>,
): LazyQueryTuple<TData, TVariables> {
  const [execute, result] = apolloUseLazyQuery<TData, TVariables>(query, options);
  const resolveRef = useRef<(value: LazyQueryResult<TData, TVariables>) => void>();

  useEffect(() => {
    if (result.called && !result.loading && resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = undefined;
    }
  }, [result, resolveRef]);

  const queryLazily: LazyQueryExecute<TData, TVariables> = useCallback(
    args => {
      execute(args);
      return new Promise<LazyQueryResult<TData, TVariables>>(resolve => {
        resolveRef.current = resolve;
      });
    },
    [execute],
  );

  return [queryLazily, result];
}

/**
 * Use state which is tied to a query parameter.
 *
 * Optionally persisting of the value to the query parameter can be debounced. This is useful when you want to persist
 * search queries to the query parameters. Because it will avoid adding an entry to the history for every key stroke.
 *
 * @param key The key on which this state is persisted to the query string.
 * @param initialValue The initial value if there is no key in the query string.
 * @param debounce Optional debounce timeout.
 */
export function useQueryString(key: string, initialValue: string, debounce = 0): [string, (v: string) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const queryParam = searchParams.get(key);

  const [value, setValue] = useState(searchParams.get(key) || initialValue);

  const updateQueryParam = useCallback(
    (value: string) => {
      if (value) {
        searchParams.set(key, value);
      } else {
        searchParams.delete(key);
      }

      setSearchParams(searchParams);
    },
    [setSearchParams, key, searchParams],
  );
  const updateQueryParamDebounced = useDebouncedCallback(updateQueryParam, debounce);

  // On update of the query parameter also update the state.
  useEffect(() => {
    if (queryParam) {
      setValue(queryParam);
    } else {
      setValue(initialValue);
    }
  }, [setValue, queryParam, initialValue]);

  // On update of the state also update the query parameter.
  const onSetValue = useCallback(
    (value: string) => {
      setValue(value);

      if (debounce > 0) {
        updateQueryParamDebounced(value);
      } else {
        updateQueryParam(value);
      }
    },
    [debounce, updateQueryParam, updateQueryParamDebounced],
  );

  return [value, onSetValue];
}

/**
 * Pass this hook a resize handle and it will invoke the callback with the deltaX and deltaY coordinates when dragging.
 *
 * @param ref The reference to the resize handler.
 * @param callback Callback to invoke on resize, make sure this callback doesn't update.
 * @param onStart Callback invoked when the user starts resizing.
 * @param onStop Callback invoked when the user stops resizing.
 */
export function useResizeHandler(
  ref: React.MutableRefObject<HTMLElement | null>,
  callback: (deltaX: number, deltaY: number) => void,
  onStart?: () => void,
  onStop?: () => void,
) {
  const state = useRef({
    dragging: false,
    startX: 0,
    startY: 0,
  });

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (state.current.dragging) {
        callback(e.clientX - state.current.startX, e.clientY - state.current.startY);

        state.current.startX = e.clientX;
        state.current.startY = e.clientY;
      }
    };

    const onMouseUp = () => {
      if (state.current.dragging) {
        state.current.dragging = false;

        if (onStop) {
          onStop();
        }
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      if (ref.current && e.target && e.target instanceof Element && ref.current.contains(e.target)) {
        e.preventDefault();
        if (onStart) {
          onStart();
        }

        state.current.dragging = true;
        state.current.startX = e.clientX;
        state.current.startY = e.clientY;
      }
    };

    document.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    document.addEventListener('mousedown', onMouseDown);

    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
      document.removeEventListener('mousedown', onMouseDown);
    };
  }, [callback, onStart, onStop, ref]);
}

/**
 * A hook that can toggle a boolean value.
 *
 * @param defaultValue The default value for the toggle.
 */
export function useToggle(defaultValue?: boolean): [boolean, { toggle: () => void; set: (v: boolean) => void }] {
  const [state, setState] = useState(defaultValue ?? false);
  return [state, { toggle: () => setState(!state), set: setState }];
}

/**
 * This variant of the useEffect hook invokes `fn` only once even though its depedencies might change.
 *
 * @param callback The function to invoke on effect.
 * @param deps The dependencies.
 * @param condition This function determines whether to invoke the callback.
 */
export function useEffectOnce(callback: () => void, deps: any[], condition: () => undefined | boolean) {
  const called = useRef(false);
  useEffect(() => {
    if (called.current) return;
    if (condition()) {
      callback();
      called.current = true;
    }
  }, [callback, condition, deps]);
}

/**
 * A variant of useState which resets to the initial value after a given timeout.
 *
 * @param initialValue
 * @param ms Timeout in milliseconds after which to reset to the initial value.
 */
export function useResettingState<T>(initialValue: T, ms: number): [T, (value: T) => void] {
  const setTimeout = useSetTimeout();
  const [value, setValue] = useState(initialValue);

  return [
    value,
    v => {
      setValue(v);
      setTimeout(() => setValue(initialValue), ms);
    },
  ];
}

/**
 * A hook to track the current page index in a query parameter.
 *
 * @param key of the query parameter containing the current page index.
 * @param initialValue of the page index.
 */
export function useQueryParamPagination(key: string, initialValue?: number) {
  const [queryParam, setQueryParam] = useQueryString(key, initialValue ? String(initialValue) : '');
  const pageIndex = parseInt(queryParam, 10) || 0;
  const setPageIndex = (index: number) => setQueryParam(String(index));

  return {
    pageIndex,
    nextPage: () => {
      setPageIndex(pageIndex + 1);
    },
    previousPage: () => {
      setPageIndex(pageIndex - 1);
    },
    gotoPage: (index: number) => {
      if (index > 0) {
        setPageIndex(index);
      } else {
        setQueryParam('');
      }
    },
  };
}

/**
 * A hook to manage session storage as state.
 *
 * @param key defines the unique key used to identify which session storage key to read to and write from.
 * @param initialValue optional initial state value.
 */
export const useSessionStorage: UseSomeStorage = <TData extends unknown>(
  key: string,
  options: UseStorageOptions<TData> = {},
  fallbackStorage: Storage = memoryStorage,
) => {
  try {
    return useStorage(key, window.sessionStorage, options);
  } catch {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useStorage(key, fallbackStorage, options);
  }
};

export interface UseStorage {
  // Convenience overload for calls without options
  <TData>(key: string, storage: Storage): UseStorageResult<TData | null>;

  // Convenience overload for calls with null / undefined initialValue
  <TData>(key: string, storage: Storage, opts: UseStorageOptions<null | undefined>): UseStorageResult<TData | null>;

  // Convenience overload for calls with initialValue
  <TData>(key: string, storage: Storage, opts: UseStorageOptions<TData>): UseStorageResult<TData>;
}

export interface UseSomeStorage {
  // Convenience overload for calls without options
  <TData>(key: string): UseStorageResult<TData | null>;

  // Convenience overload for calls with null / undefined initialValue
  <TData>(key: string, opts: UseStorageOptions<null | undefined>, fallback?: Storage): UseStorageResult<TData | null>;

  // Convenience overload for calls with initialValue
  <TData>(key: string, opts: UseStorageOptions<TData>, fallback?: Storage): UseStorageResult<TData>;
}

export interface UseStorageOptions<TData> {
  initialValue?: TData;
  reviver?: Parameters<typeof JSON.parse>[1];
}

type UseStorageResult<TData> = [value: TData, setValue: (value: TData) => void, clear: () => void];

/**
 * Base hook that implements standard logic of handling Storage intereaction
 * @param key defines the unique key used to identify which session storage key to read to and write from.
 * @param storage storage to access. eg. window.localStorage
 * @param initialValue optional initial state value.
 */
export const useStorage: UseStorage = <TData = unknown>(
  key: string,
  storage: Storage,
  { initialValue, reviver }: UseStorageOptions<TData> = {},
) => {
  const getValue = useCallback(
    function () {
      let storageValue;

      try {
        storageValue = storage.getItem(key);
      } catch (error) {
        // If user is in private mode or has storage restriction
        // storage can throw.
        return initialValue ?? null;
      }

      if (storageValue === null) {
        return initialValue ?? null;
      }

      try {
        return JSON.parse(storageValue, reviver);
      } catch (error) {
        // Stored value is not a valid JSON object, return
        // the stored value as a string. This can happen, if the
        // stored value is just a string.
        return storageValue === 'undefined' ? null : storageValue;
      }
    },
    [storage, key, initialValue, reviver],
  );

  const setValue = useCallback(
    function (value: TData | null) {
      try {
        if (value === null) {
          storage.removeItem(key);
        } else {
          storage.setItem(key, typeof value !== 'string' ? JSON.stringify(value) : value);
        }

        // Notify all useStorage hooks of a change
        window.dispatchEvent(new Event('local-storage'));
      } catch {
        // If user is in private mode or has storage restriction
        // storage can throw. Also JSON.stringify can throw.
      }
    },
    [storage, key],
  );

  const clearValue = useCallback(() => setValue(null), [setValue]);
  const [state, setState] = useState<TData | null>(getValue());

  useEffect(() => {
    const handleStorageChange = () => setState(getValue());

    // Keeps all useStorage hooks in sync with each other
    window.addEventListener('local-storage', handleStorageChange);
    return () => window.removeEventListener('local-storage', handleStorageChange);
  }, [getValue]);

  return [state, setValue, clearValue];
};

let idCounter = 0;

/**
 * Provides a unique ID that stays stable across re-renders. Useful for
 * accessibility, SVG effects and other purposes.
 */
export function useUniqueID(prefix = '') {
  const [uniqueID] = useState(() => idCounter++);
  return `${prefix}${uniqueID}`;
}

/**
 * Prompts the user before leaving/reloading the page using the browser controls.
 *
 * Won't trigger on react-router navigation.
 *
 * @param when - Whether a confirmation prompts should be shown when the user
 *     leaves/reloads the page.
 * @param message - The message to prompt the user with. Won't work in most of
 *     the modern browsers.
 */
export function useBeforeUnload(when: boolean, message = '') {
  useEffect(() => {
    if (!when) {
      return;
    }

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      // `event.preventDefault` is the standard way of activating the
      // confirmation modal:
      event.preventDefault();

      // Setting `event.returnValue` and returning a value is the de facto way
      // of activating the confirmation modal.
      //
      // The browsers are also supposed to use `returnValue` as the modal's
      // content, but most of them actually won't because of
      // https://bugs.chromium.org/p/chromium/issues/detail?id=587940
      event.returnValue = message;
      return message;
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [message, when]);
}

export const useDidUpdateEffect = (effect: React.EffectCallback, deps?: React.DependencyList) => {
  const didMountRef = useRef(false);

  useEffect(() => {
    if (didMountRef.current) {
      return effect();
    }

    didMountRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};
